import {useEffect, useRef, useState} from 'react';
import {sendForReview} from './api';

type Props = {
  organizationId: string;
  orderId: string;
  version: number;
  onComplete?: () => void;
  actionBusy?: boolean;
  onBeginAction?: () => boolean;
  onEndAction?: () => void;
};

export function AttorneyReviewAction({organizationId, orderId, version, onComplete, actionBusy = false, onBeginAction, onEndAction}: Props) {
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState('Save complete validated draft details before continuing.');
  const [complete, setComplete] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const attempted = useRef(false);
  const statusRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (complete || uncertain) statusRef.current?.focus(); }, [complete, uncertain]);

  async function submit() {
    if (attempted.current || pending || complete || actionBusy) return;
    if (onBeginAction?.() === false) return;
    attempted.current = true;
    setPending(true);
    setStatus('Rechecking the draft version and organization scope.');
    try {
      const receipt = await sendForReview(organizationId, orderId, version);
      if (!Number.isSafeInteger(receipt.version) || receipt.version !== version + 1) throw new Error('Review response version mismatch.');
      setComplete(true);
      setStatus('Your booking has been sent to ProFenz staff for review. No server has been assigned, service attempted, court filing made, or charge collected.');
      onComplete?.();
    } catch {
      setUncertain(true);
      setStatus('The submission result could not be confirmed. Reload this page to check the draft before trying again. No assignment, dispatch or charge was requested.');
    } finally {
      setPending(false);
      onEndAction?.();
    }
  }

  return (
    <section className="panel" aria-labelledby="review-title">
      <p className="eyebrow">VALIDATED INTAKE</p>
      <h2 id="review-title">Submit your booking</h2>
      <p>This sends your completed booking for staff review. It does not assign a server, dispatch, start service, or charge you.</p>
      <button type="button" disabled={pending || complete || uncertain || actionBusy} onClick={submit}>
        {complete ? 'Submitted for review' : pending ? 'Sending…' : uncertain ? 'Review status unconfirmed' : 'Submit booking for review'}
      </button>
      <p className="notice" role="status" aria-live="polite" aria-atomic="true" tabIndex={-1} ref={statusRef}>{status}</p>
    </section>
  );
}
